import { BaseModel } from "./BaseModel";
import { ColumnHeaders, TabNames, Currency } from '../modules/Constants';

export class MEExpenseModel extends BaseModel {
    supplementalFormatting(formatting) {

        formatting.columns[4].width = 300;
        formatting.columns[5].width = 300;
        formatting.columns[6].width = 150;

        return formatting;
    }

    constructor() {
        super();
        this.TabName = TabNames.ME_EXPENSE;
        this.TabIndex = 5;
        this.DataProcessor = new MEExpenseProcessor();
    }
}

export class MEExpenseProcessor {
    ExchangeRates;
    Years;
    Currency;
    Records;
    Headers;

    process(data) {

        this.ExchangeRates = [];
        this.Years = [];
        this.Records = [];
        this.Headers = [];
        this.Currency = data.currency;

        let study = data.study;
        let snapshots = data.snapshots;
        let version = data.version;


        study.exchangeRates.forEach(rate => {
            rate.Item2.Values.forEach(item => {
                this.ExchangeRates.push({
                    currency: rate.Item1,
                    year: new Date(item.Date).getUTCFullYear(),
                    value: item.Value
                });
            });
        });


        let firstSOPYear = Math.min.apply(Math, snapshots.map(snapshot => snapshot.SaleableParts?.map(part => new Date(part?.CustomerSOP?.Value.Fields[0]).getUTCFullYear())).reduce(function (a, b) { return a.concat(b) }, []));
        let firstYearSpend = firstSOPYear - (version?.SpendYearsPriorToSOP?.Value?.Fields[0] ?? 2);
        let lastYearSpend = firstSOPYear + (version?.SpendYearsAfterSOP?.Value?.Fields[0] ?? 0);
        snapshots.forEach(snapshot => {
            snapshot.SaleableParts.forEach(part => {
                let endDate = part.CustomerVolume?.Value?.Fields[0]?.EndDate;
                if (endDate) {
                    let year = new Date(endDate).getUTCFullYear();
                    if (year > lastYearSpend) {
                        lastYearSpend = year;
                    }
                }
            });
        })

        var yearsSpending = Array.from(Array((lastYearSpend - firstYearSpend) + 1), (_, x) => x + firstYearSpend);
        this.Years = yearsSpending;


        //JG - Update Years in case there are additional years outside of Spending Years.
        snapshots.forEach(snapshot => {
            snapshot?.Manufacturing.forEach(manufacturing => {
                manufacturing.Operations.forEach(op => {
                    let spendTiming = op.SpendTiming;

                    let spendTimingYears = spendTiming?.Value?.Fields[0]?.Values;

                    spendTimingYears.forEach(spendTimingYear => {
                        let year = new Date(spendTimingYear.Date).getUTCFullYear();

                            if (this.Years.findIndex(x => x === year) <= -1) {
                                this.Years.push(year);
                            }
                    })
                })
            });
        });


        this.Years.sort();

        snapshots.forEach(snapshot => {
            this.setRecords(snapshot);
        });

        this.configureHeader();
        let result = {
            headers: this.Headers,
            display: this.getDisplay(),
            numerics: this.getNumerics()
        };


        return result;
    }

    setRecords(snapshot) {
        snapshot?.Manufacturing.forEach(manufacturing => {
            manufacturing.Operations.forEach(op => {
                if (op.ManufacturingExpenseInvestment?.length < 1) {
                    this.Records.push(new MEExpenseRowModel(this.Years, snapshot, manufacturing, op, null, this.ExchangeRates, this.Currency));
                    return;
                }

                op.ManufacturingExpenseInvestment.forEach(mfgExpenseInvestment => {
                    this.Records.push(new MEExpenseRowModel(this.Years, snapshot, manufacturing, op, mfgExpenseInvestment, this.ExchangeRates, this.Currency));
                })
            })
        });
    }

    configureHeader() {
        let header = [
            ColumnHeaders.OPTION_NAME,
            ColumnHeaders.PLANT,
            ColumnHeaders.MFG_PRO_NAME,
            ColumnHeaders.OPERATION_NUMBER,
            ColumnHeaders.OPERATION_DESCRIPTION,
            ColumnHeaders.EXPENSE_INVESTMENT_DESCRIPTION,
            ColumnHeaders.EXPENSE_INVESTMENT_QTY
        ];

        this.Years.forEach(year => {
            header.push(year + " Cost");
        });

        header.push(
            ColumnHeaders.START_YEAR,
            ColumnHeaders.CURRENCY_PREFIX
        )

        this.Years.forEach(year => {
            header.push(`${year} Cost ${ColumnHeaders.CONVERTED_PREFIX}`);
        });

        header.push(
            ColumnHeaders.TOTAL_EXPENSE_INVESTMENT_COST,
            ColumnHeaders.START_YEAR,
            ColumnHeaders.CURRENCY_PREFIX
        )

        this.Headers = header;
    }

    getDisplay() {
        let display = [];

        display.push(this.Headers);

        this.Records.forEach(record => {
            let row = [
                record.OptionName,
                record.Plant,
                record.MfgProName,
                record.OperationNumber,
                record.OperationDescription,
                record.ExpenseInvestmentDescription,
                record.ExpenseInvestmentQuantity
            ];

            record.Cost.forEach(cost => {
                row.push(cost);
            });

            row.push(
                this.Years[0],
                record.Currency
            )

            record.CostConverted.forEach(cost => {
                row.push(cost);
            });

            var total = record.CostConvertedTotal;

            row.push(
                Number.isNaN(total) ? "" : total, //Total Cost (Converted)
                this.Years[0],
                this.Currency // Currency (Converted)
            )

            display.push(row);
        });

        return display;
    }

    getNumerics() {
        let numeric = [];

        //set number formatting
        let startCost = 7;
        let endCost = this.Years.length + startCost - 1;
        let startConvCost = endCost + 3;
        let endConvCost = this.Years.length + startConvCost;

        numeric.push({ start: startCost, end: endCost });
        numeric.push({ start: startConvCost, end: endConvCost });

        return numeric;
    }

}

export class MEExpenseRowModel {
    OptionName = "";
    Plant = "";
    MfgProName = "";
    OperationNumber = "";
    OperationDescription = "";
    ToolingInvestmentDescription = "";
    ToolingInvestmentQuantity = "";
    ToolingInvestmentIsRebillable = "";
    ToolingInvestmentElectedRebillPercent = "";
    ToolingInvestmnentToolLifePcs = "";
    ToolingInvestmentToolReplacementCost = "";
    ToolingInvestmentToolReplacementCostCurrency = "";
    LeadTimeWeeks = "";
    Cost = [];
    CostConverted = [];
    CostConvertedTotal = 0;
    Currency = "";

    constructor(years, snapshot, manufacturing, operation, mfgExpenseInvestment, exchangeRates, exchangeCurrency) {

        this.OptionName = snapshot.GroupName;
        this.Plant = manufacturing?.Plant?.Value?.Fields[0]?.Number?.Fields[0];
        this.MfgProName = manufacturing?.Name?.Value?.Fields[0];
        this.OperationNumber = operation?.OperationNumber?.Fields[0];
        this.OperationDescription = operation?.Description?.Value?.Fields[0];
        this.ExpenseInvestmentDescription = mfgExpenseInvestment?.Description?.Value?.Fields[0];
        this.ExpenseInvestmentQuantity = mfgExpenseInvestment?.Quantity?.Value?.Fields[0];
        this.Cost = [];
        this.CostConverted = [];
        this.CostConvertedTotal = 0;
        this.Currency = mfgExpenseInvestment?.Cost?.Value?.Fields[0]?.Tag;

        if (this.Currency === '') {
            this.Currency = Currency.USD;
        }

        let spendTiming = operation?.SpendTiming?.Value?.Fields[0]?.Values;

        years.forEach(year => {
            let cost = undefined;
            let convCost = undefined;

            if (mfgExpenseInvestment) {
                let spendTimingYear = spendTiming.find(x => new Date(x.Date).getUTCFullYear() === year);
                let mfgExpenseInvestmentTotalCostValues = mfgExpenseInvestment?.Cost?.Value?.Fields[0]?.Values?.map(item => item.Value);
                let mfgExpenseInvestmentTotalCost = mfgExpenseInvestmentTotalCostValues?.length > 0 ? mfgExpenseInvestmentTotalCostValues?.reduce((prev, next) => prev + next) : 0;
                cost = mfgExpenseInvestmentTotalCost * (spendTimingYear?.Value ?? 0);

                let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === exchangeCurrency);
                let currentCurrencyRate = exchangeRates?.find(rate => rate.year === year && rate.currency === this.Currency);

                if (exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined) {
                    if (cost !== undefined) {
                        this.CostConverted.push("Error");
                    } else {
                        this.CostConverted.push("");
                    }
                } else {
                    convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                    this.CostConvertedTotal += convCost;
                }
            }

            this.Cost.push(cost);
            this.CostConverted.push(convCost);

        });

        this.CostConvertedTotal = this.CostConvertedTotal * this.ExpenseInvestmentQuantity;

    }
}